'use client';

import * as React from 'react';
import YouTube from 'react-youtube';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@workspace/ui/components/dialog';

interface VideoDialogProps {
    children: React.ReactNode;
    videoId: string;
    title?: string;
}

export function VideoDialog({ children, videoId, title = "Demo Video" }: VideoDialogProps) {
    const [open, setOpen] = React.useState(false);
    const [error, setError] = React.useState(false);

    // Reset error when dialog opens or video ID changes
    React.useEffect(() => {
        if (open) {
            setError(false);
        }
    }, [open, videoId]);

    // Extract video ID from URL if a full URL is provided
    const getVideoId = React.useMemo(() => {
        if (!videoId) return '';

        // If it's already just an ID (no slashes, no query params), return it
        if (!videoId.includes('/') && !videoId.includes('?')) {
            return videoId;
        }

        // Handle youtu.be short URLs
        if (videoId.includes('youtu.be/')) {
            const match = videoId.match(/youtu\.be\/([^?&]+)/);
            return match ? match[1] : videoId;
        }

        // Handle youtube.com/watch?v= format
        if (videoId.includes('youtube.com')) {
            const match = videoId.match(/[?&]v=([^&]+)/);
            return match ? match[1] : videoId;
        }

        return videoId;
    }, [videoId]);

    const opts = React.useMemo(() => ({
        width: '100%',
        height: '100%',
        playerVars: {
            // Removed autoplay to avoid Error 153 - user can click play
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
        },
    }), []);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="max-w-4xl w-full p-0">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
                </DialogHeader>
                <div className="relative w-full aspect-video p-6 pt-0">
                    {open && getVideoId && (
                        <div className="absolute inset-0 w-full h-full rounded-lg overflow-hidden">
                            {error ? (
                                <div className="flex flex-col items-center justify-center h-full bg-muted rounded-lg p-6">
                                    <p className="text-muted-foreground mb-4 text-center">
                                        Unable to load video. The video may not allow embedding.
                                    </p>
                                    <a
                                        href={`https://www.youtube.com/watch?v=${getVideoId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        Watch on YouTube →
                                    </a>
                                </div>
                            ) : (
                                <YouTube
                                    videoId={getVideoId}
                                    opts={opts}
                                    className="w-full h-full"
                                    iframeClassName="w-full h-full rounded-lg"
                                    onError={() => setError(true)}
                                />
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
